/**
 * Help, ported from the approved prototype (docs/prototype.html "HELP: little (i) icons … plus a full
 * walkthrough"): the Guide button's walkthrough panel, the "New here?" banner, and the (i) icon with its
 * pop-over on page titles.
 *
 * What shows is decided by GET /me → `menu`, never by a role (ADR §1.4): a walkthrough topic appears only when
 * the menu items or hub tabs it is about are in the caller's menu, "Take me there" goes to that item's route
 * from the menu, and an (i) appears only where help copy exists for the page's tab or item key.
 */
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router';
import { ROLE_LABEL, type MenuItem } from '@amplispot/contracts';
import { useSession } from '../lib/session';
import { Icon } from '../ui/Icon';
import { GUIDE, HELP, helpForLabel, type GuideTopic, type HelpEntry } from './help-content';
import { readPref, writePref } from './prefs';

interface Topic {
  topic: GuideTopic;
  /** Where "Take me there" goes. */
  route: string;
}

/** Menu item keys and hub tab keys → route, so a topic can ask "is this page in my menu?". */
function routesByKey(menu: MenuItem[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const item of menu) m.set(item.key, item.route);
  for (const item of menu) for (const t of item.tabs) if (!m.has(t.key)) m.set(t.key, t.route);
  return m;
}

export function visibleTopics(menu: MenuItem[], homeRoute: string): Topic[] {
  const routes = routesByKey(menu);
  const out: Topic[] = [];
  for (const topic of GUIDE) {
    const present = topic.go.every((k) => routes.has(k)) && (topic.needs ?? []).every((k) => routes.has(k)) && !(topic.absent ?? []).some((k) => routes.has(k));
    if (!present) continue;
    out.push({ topic, route: (topic.go[0] ? routes.get(topic.go[0]) : undefined) ?? homeRoute });
  }
  return out;
}

interface InfoState {
  btn: HTMLElement;
  /** Page help key; also picks the walkthrough topic behind "See the full guide". Empty for a label's (i). */
  helpKey: string;
  /** The copy to show. A page (i) looks it up by key; a label (i) passes the entry its text matched. */
  entry: HelpEntry;
}

interface HelpApi {
  openGuide: (topicId?: string) => void;
  openInfo: (btn: HTMLElement, helpKey: string, entry: HelpEntry) => void;
  seen: boolean;
  markSeen: () => void;
}

const Ctx = createContext<HelpApi | null>(null);

function useHelp(): HelpApi {
  const c = useContext(Ctx);
  if (!c) throw new Error('useHelp must be used inside HelpProvider');
  return c;
}

const isEditable = (el: Element | null) =>
  !!el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || (el as HTMLElement).isContentEditable);

export function HelpProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const userId = me.user.id;
  const loc = useLocation();
  const topics = useMemo(() => visibleTopics(me.menu, me.home_route), [me.menu, me.home_route]);

  const [seen, setSeen] = useState(() => readPref('guide.seen', userId) === '1');
  const [icons, setIcons] = useState(() => readPref('help.icons', userId) !== '0');
  const [guide, setGuide] = useState<{ open: boolean; topicId: string }>({ open: false, topicId: 'start' });
  const [info, setInfo] = useState<InfoState | null>(null);
  const opener = useRef<HTMLElement | null>(null);

  const markSeen = useCallback(() => {
    setSeen(true);
    writePref('guide.seen', userId, '1');
  }, [userId]);

  const openGuide = useCallback(
    (topicId?: string) => {
      setInfo(null);
      setGuide((g) => {
        if (!g.open) opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const id = topicId && topics.some((t) => t.topic.id === topicId) ? topicId : topics.some((t) => t.topic.id === g.topicId) ? g.topicId : 'start';
        return { open: true, topicId: id };
      });
    },
    [topics],
  );

  const closeGuide = useCallback((restoreFocus = true) => {
    setGuide((g) => ({ ...g, open: false }));
    if (restoreFocus) opener.current?.focus?.();
  }, []);

  const openInfo = useCallback((btn: HTMLElement, helpKey: string, entry: HelpEntry) => {
    setInfo((cur) => (cur && cur.btn === btn ? null : { btn, helpKey, entry }));
  }, []);

  // "Show (i) icons on pages": the prototype hides them with a body class.
  useEffect(() => {
    document.body.classList.toggle('noinfo', !icons);
    return () => document.body.classList.remove('noinfo');
  }, [icons]);
  useEffect(() => {
    document.body.classList.toggle('gopen', guide.open);
    return () => document.body.classList.remove('gopen');
  }, [guide.open]);

  // A pop-over belongs to the page it was opened on.
  useEffect(() => setInfo(null), [loc.pathname]);

  // Escape closes the walkthrough and the pop-over; "?" opens the walkthrough (not while typing).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (info) {
          info.btn.focus();
          setInfo(null);
        }
        if (guide.open) closeGuide();
      } else if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey && !guide.open && !isEditable(document.activeElement)) {
        e.preventDefault();
        openGuide();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [info, guide.open, closeGuide, openGuide]);

  const api = useMemo<HelpApi>(() => ({ openGuide, openInfo, seen, markSeen }), [openGuide, openInfo, seen, markSeen]);

  return (
    <Ctx.Provider value={api}>
      {children}
      {guide.open
        ? createPortal(
            <GuidePanel
              topics={topics}
              topicId={guide.topicId}
              setTopic={(id) => setGuide({ open: true, topicId: id })}
              close={closeGuide}
              icons={icons}
              setIcons={(v) => {
                setIcons(v);
                writePref('help.icons', userId, v ? '1' : '0');
              }}
            />,
            document.body,
          )
        : null}
      {info ? createPortal(<InfoPop info={info} topics={topics} close={() => setInfo(null)} />, document.body) : null}
    </Ctx.Provider>
  );
}

// ------------------------------------------------------------------ Guide button + banner

export function GuideButton() {
  const { openGuide } = useHelp();
  return (
    <button type="button" className="btn guideBtn" id="guideBtn" onClick={() => openGuide()} aria-label="Open the platform walkthrough" aria-haspopup="dialog">
      <Icon name="help" size={16} />
      <span>Guide</span>
    </button>
  );
}

/** "New here?" — prepended to the view until the person has taken or skipped the walkthrough. */
export function WelcomeBanner() {
  const { seen, markSeen, openGuide } = useHelp();
  if (seen) return null;
  return (
    <div className="note" id="gHello" role="region" aria-label="New here?" style={{ marginBottom: 14, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <div className="grow">
        <b>New here?</b> A two-minute walkthrough shows each part of the tool and how to use it. Small (i) icons also explain things as you go.
      </div>
      <button
        type="button"
        className="btn sm pri"
        onClick={() => {
          markSeen();
          openGuide('start');
        }}
      >
        Take the walkthrough
      </button>
      <button type="button" className="btn sm ghost" onClick={markSeen}>
        Not now
      </button>
    </div>
  );
}

// ------------------------------------------------------------------ (i) on a page title

/** The little (i) after a page title. Renders nothing when there is no help copy for `helpKey`. */
export function PageInfo({ helpKey }: { helpKey: string | undefined }) {
  const { openInfo } = useHelp();
  const h = helpKey ? HELP[helpKey] : undefined;
  if (!helpKey || !h) return null;
  return (
    <button
      type="button"
      className="ibtn"
      aria-label={`What is this? ${h.t}`}
      aria-haspopup="dialog"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        openInfo(e.currentTarget, helpKey, h);
      }}
    >
      i
    </button>
  );
}

// ------------------------------------------------------------------ (i) on a heading, stat label, column header or pop-up title

/**
 * The (i) after a label, from the prototype's `addInfo`: panel headings, stat labels, column headers, the
 * timesheet hero, a note's lead and modal / drawer titles get one when their text matches `HELP_TXT`.
 * Renders nothing when nothing matches, and is hidden with the others when "Show (i) icons" is off.
 */
export function LabelInfo({ text }: { text: string }) {
  // Tolerates a missing provider (a kit Modal rendered on its own in a test): no help, no icon.
  const help = useContext(Ctx);
  const h = helpForLabel(text);
  if (!h || !help) return null;
  const { openInfo } = help;
  return (
    <button
      type="button"
      className="ibtn"
      aria-label={`What is this? ${h.t}`}
      aria-haspopup="dialog"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        openInfo(e.currentTarget, '', h);
      }}
    >
      i
    </button>
  );
}

/** A label followed by its (i) when it has help copy: `<th><Lbl>Overdue</Lbl></th>`. */
export function Lbl({ children }: { children: string }) {
  return (
    <>
      {children}
      <LabelInfo text={children} />
    </>
  );
}

function InfoPop({ info, topics, close }: { info: InfoState; topics: Topic[]; close: () => void }) {
  const { openGuide } = useHelp();
  const ref = useRef<HTMLDivElement>(null);
  const h = info.entry;

  // The topic that explains this page, else "Start here" (prototype showPop).
  const gid = (info.helpKey && topics.find((t) => t.topic.go.includes(info.helpKey) || t.topic.id === info.helpKey)?.topic.id) || 'start';

  useLayoutEffect(() => {
    const d = ref.current;
    if (!d) return;
    const r = info.btn.getBoundingClientRect();
    const w = Math.min(330, window.innerWidth - 24);
    d.style.width = `${w}px`;
    d.style.left = `${Math.min(Math.max(12, r.left - 8), window.innerWidth - w - 12)}px`;
    const y = r.bottom + 8;
    d.style.top = `${y}px`;
    const hh = d.getBoundingClientRect().height;
    if (y + hh > window.innerHeight - 12) d.style.top = `${Math.max(12, r.top - hh - 8)}px`;
  }, [info]);

  useEffect(() => {
    const away = (e: MouseEvent) => {
      const t = e.target as Element;
      if (ref.current?.contains(t) || t.closest('.ibtn')) return;
      close();
    };
    document.addEventListener('mousedown', away);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', away);
      window.removeEventListener('resize', close);
    };
  }, [close]);

  if (!h) return null;
  return (
    <div className="ipop" id="ipop" ref={ref} role="dialog" aria-label={h.t}>
      <div className="ipt">{h.t}</div>
      <div>{h.b}</div>
      {h.l ? (
        <ul>
          {h.l.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      ) : null}
      <div className="ipf">
        <a
          href="#guide"
          onClick={(e) => {
            e.preventDefault();
            openGuide(gid);
          }}
        >
          See the full guide
        </a>
        <button
          type="button"
          className="btn sm ghost"
          onClick={() => {
            info.btn.focus();
            close();
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ the walkthrough panel

const matches = (g: GuideTopic, q: string) => !q || `${g.t} ${g.sub} ${g.steps.join(' ')}`.toLowerCase().includes(q);

function GuidePanel({
  topics,
  topicId,
  setTopic,
  close,
  icons,
  setIcons,
}: {
  topics: Topic[];
  topicId: string;
  setTopic: (id: string) => void;
  close: (restoreFocus?: boolean) => void;
  icons: boolean;
  setIcons: (v: boolean) => void;
}) {
  const { me } = useSession();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const panel = useRef<HTMLDivElement>(null);
  const main = useRef<HTMLDivElement>(null);

  const idx = Math.max(0, topics.findIndex((t) => t.topic.id === topicId));
  const cur = topics[idx]!;
  const g = cur.topic;
  const shown = topics.filter((t) => matches(t.topic, q.trim().toLowerCase()));
  const prev = idx > 0 ? topics[idx - 1]! : null;
  const next = idx < topics.length - 1 ? topics[idx + 1]! : null;

  useEffect(() => {
    panel.current?.focus();
  }, []);
  useEffect(() => {
    main.current?.scrollTo(0, 0);
  }, [topicId]);

  // Keep Tab inside the dialog while it is open.
  const trap = (e: ReactKeyboardEvent) => {
    if (e.key !== 'Tab' || !panel.current) return;
    const f = Array.from(panel.current.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), a[href]'));
    if (f.length === 0) return;
    const first = f[0]!;
    const last = f[f.length - 1]!;
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="gbox on" id="guideBox">
      <div className="gscrim" onClick={() => close()} />
      <div className="gpanel" role="dialog" aria-modal="true" aria-label="Platform walkthrough" ref={panel} tabIndex={-1} onKeyDown={trap} style={{ outline: 'none' }}>
        <div className="gside">
          <div className="gh">
            <b>Walkthrough</b>
            <span className="sub">
              {me.user.name}, {ROLE_LABEL[me.user.role]}
            </span>
          </div>
          <input className="in" id="gQ" placeholder="Search the guide" aria-label="Search the guide" value={q} onChange={(e) => setQ(e.target.value)} />
          <div id="gList" className="glist">
            {shown.length ? (
              shown.map((t) => (
                <button type="button" key={t.topic.id} className={`gitem${t.topic.id === g.id ? ' on' : ''}`} aria-current={t.topic.id === g.id ? 'true' : undefined} onClick={() => setTopic(t.topic.id)}>
                  <b>{t.topic.t}</b>
                  <small>{t.topic.sub}</small>
                </button>
              ))
            ) : (
              <div className="sub" style={{ padding: 10 }}>
                Nothing matches. Try another word.
              </div>
            )}
          </div>
          <label className="gtog">
            <input type="checkbox" checked={icons} onChange={(e) => setIcons(e.target.checked)} /> Show (i) icons on pages
          </label>
        </div>
        <div className="gmain" ref={main}>
          <button type="button" className="x" onClick={() => close()} aria-label="Close">
            ✕
          </button>
          <div className="gtitle">{g.t}</div>
          <div className="gsub">{g.sub}</div>
          <div className="glbl">How to use it</div>
          <ol className="gsteps">
            {g.steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
          <div className="glbl">What is tracked</div>
          <div className="gtracks">
            {g.tracks.map((s) => (
              <span key={s} className="chip">
                {s}
              </span>
            ))}
          </div>
          <div className="gfoot">
            {prev ? (
              <button type="button" className="btn" onClick={() => setTopic(prev.topic.id)}>
                Back
              </button>
            ) : null}
            <div className="grow" />
            <button
              type="button"
              className="btn"
              onClick={() => {
                close(false);
                nav(cur.route);
              }}
            >
              Take me there
            </button>
            {next ? (
              <button type="button" className="btn pri" onClick={() => setTopic(next.topic.id)}>
                Next: {next.topic.t}
              </button>
            ) : (
              <button type="button" className="btn pri" onClick={() => close()}>
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
